<script setup lang="ts">
import { ref, watch, watchEffect } from 'vue';
import { SearchFilter, FilterOption } from '../../../search-panel-types';
import FilterFieldLayout from './FilterFieldLayout.vue';

const emit = defineEmits<{
  (e: 'filters-updated', filter: SearchFilter): void;
}>();

type Props = {
  items: FilterOption[];
  fieldKey: string;
  label: string;
  selected: { value: SearchFilter };
  customRange: number[] | null;
  customLabel?: boolean;
  multiple?: boolean;
};
const props = defineProps<Props>();

// Do not allow negative values
const findMinRangeValue = () =>
  Math.max(
    props.items.reduce(
      (prev: number | null, cur: FilterOption) => (Number(prev) < Number(cur.value) ? prev : Number(cur.value)),
      null,
    ) ?? 0,
    0,
  );

const findMaxRangeValue = () =>
  props.items.reduce(
    (prev: number | null, cur: FilterOption) => (Number(prev) > Number(cur.value) ? prev : Number(cur.value)),
    null,
  ) ?? 0;

const minRangeValue = ref(findMinRangeValue());
const maxRangeValue = ref(findMaxRangeValue());
const defaultRange = [minRangeValue.value, maxRangeValue.value];
const range = ref<number[]>([]);
const foundedMinRangeValue = ref<number>(minRangeValue.value);
const foundedMaxRangeValue = ref<number>(maxRangeValue.value);

range.value = defaultRange;
watch(
  () => props.customRange,
  () => {
    if (props.customRange !== null) {
      range.value = props.customRange;
      return;
    }
    range.value = defaultRange;
  },
);

watchEffect(() => {
  if (props.customRange && props.customRange.length === 0) {
    foundedMinRangeValue.value = findMinRangeValue();
    foundedMaxRangeValue.value = findMaxRangeValue();
    if (Number(foundedMaxRangeValue.value) > maxRangeValue.value) {
      maxRangeValue.value = Number(props.customRange[1]);
    }
    if (Number(foundedMinRangeValue.value) < minRangeValue.value) {
      minRangeValue.value = Number(props.customRange[0]);
    }
    if (!props.selected.value[props.fieldKey]) {
      range.value = [minRangeValue.value, maxRangeValue.value];
    }
  } else {
    foundedMinRangeValue.value = props.customRange !== null ? props.customRange[0] : minRangeValue.value;
    foundedMaxRangeValue.value = props.customRange !== null ? props.customRange[1] : maxRangeValue.value;
    // if we get new values from api we need to setup right range but only for cases when the range is less
    // than minimum or more than maximum
    if (Number(foundedMaxRangeValue.value) > maxRangeValue.value && props.customRange) {
      maxRangeValue.value = Number(props.customRange[1]);
    }
    if (Number(foundedMinRangeValue.value) < minRangeValue.value && props.customRange) {
      minRangeValue.value = Number(props.customRange[0]);
    }
    if (!props.selected.value[props.fieldKey]) {
      if (props.customRange) {
        range.value = props.customRange;
      } else {
        range.value = [minRangeValue.value, maxRangeValue.value];
      }
    }
  }
});

const handleUpdate = (rangeValue: number[]) => {
  emit('filters-updated', {
    [props.fieldKey]: [
      { value: String(rangeValue[0]), label: '', count: 1 },
      { value: String(rangeValue[1]), label: '', count: 1 },
    ],
  });
};
</script>

<template>
  <template v-if="!props.multiple">
    <FilterFieldLayout v-show="props.items?.length > 0" :label="props.label ?? ''">
      <template #content>
        <div class="range-container">
          <v-range-slider
            v-model="range"
            color="primary"
            hide-details
            thumb-label="always"
            :max="maxRangeValue"
            :min="minRangeValue"
            step="1"
            track-size="1"
            thumb-size="12"
            @end="handleUpdate"
          >
            <template #thumb-label="rangeProps">
              <slot v-if="props.customLabel" :value="rangeProps.modelValue"></slot>
              <span v-else>
                {{ rangeProps.modelValue }}
              </span>
            </template>
          </v-range-slider>
        </div>
      </template>
    </FilterFieldLayout>
  </template>
  <template v-else>
    <div class="range-container">
      <v-range-slider
        v-model="range"
        color="primary"
        hide-details
        thumb-label="always"
        :max="maxRangeValue"
        :min="minRangeValue"
        step="1"
        track-size="1"
        thumb-size="12"
        @end="handleUpdate"
      >
        <template #thumb-label="rangeProps">
          <slot v-if="props.customLabel" :value="rangeProps.modelValue"></slot>
          <span v-else>
            {{ rangeProps.modelValue }}
          </span>
        </template>
      </v-range-slider>
    </div>
  </template>
</template>

<style scoped lang="scss">
.range-container {
  padding: 35px 10px 15px 10px;
}
:deep(.v-slider-thumb__label) {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  &:before {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
